/**
 * Skeleton loading components — shimmer effect, not spinners
 */

interface SkeletonProps {
  className?: string;
}

export function Skeleton({ className = '' }: SkeletonProps) {
  return (
    <div className={`animate-pulse bg-slate-200 dark:bg-slate-700 rounded-[var(--radius-md)] ${className}`} />
  );
}

export function SkeletonCard() {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-[var(--radius-lg)] border border-slate-200 dark:border-slate-700 p-4 space-y-3">
      <Skeleton className="h-4 w-24" />
      <Skeleton className="h-8 w-32" />
      <Skeleton className="h-3 w-full" />
    </div>
  );
}

export function SkeletonExpenseRow() {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-[var(--radius-lg)] border border-slate-200 dark:border-slate-700 p-3 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <Skeleton className="w-2 h-8 rounded-full" />
        <div className="space-y-2">
          <Skeleton className="h-4 w-36" />
          <Skeleton className="h-3 w-48" />
        </div>
      </div>
      <div className="text-right space-y-2">
        <Skeleton className="h-4 w-16 ml-auto" />
        <Skeleton className="h-5 w-20 ml-auto rounded-full" />
      </div>
    </div>
  );
}

export function SkeletonDashboard() {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-4 w-48" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SkeletonCard /><SkeletonCard /><SkeletonCard /><SkeletonCard />
      </div>
      <SkeletonCard />
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="space-y-2"><SkeletonExpenseRow /><SkeletonExpenseRow /><SkeletonExpenseRow /></div>
        <div className="space-y-2"><SkeletonCard /><SkeletonCard /></div>
      </div>
    </div>
  );
}

export function SkeletonTable({ rows = 5 }: { rows?: number }) {
  return (
    <div className="space-y-2">
      {Array.from({ length: rows }).map((_, i) => (
        <SkeletonExpenseRow key={i} />
      ))}
    </div>
  );
}

/**
 * Detail-page skeleton — header with title + subtitle, KPI strip, then
 * a content area. Matches the typical /hr/:id, /finance/bills/:id,
 * /projects/:id, /contracts/:id shape so the user's eye lands on the
 * eventual content's position immediately. NS4 UI polish R1 — replaces
 * bare `<p>Loading…</p>` returns across 29 detail / sub pages.
 */
export function DetailPageSkeleton() {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="space-y-2 flex-1 min-w-0">
          <Skeleton className="h-7 w-72 max-w-full" />
          <Skeleton className="h-3 w-96 max-w-full" />
        </div>
        <div className="flex gap-2 shrink-0">
          <Skeleton className="h-9 w-24" />
          <Skeleton className="h-9 w-24" />
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </div>

      {/* Content area */}
      <div className="space-y-2">
        <Skeleton className="h-12" />
        <Skeleton className="h-12" />
        <Skeleton className="h-12" />
      </div>
    </div>
  );
}

/**
 * Compact skeleton for inline status (e.g., a single section loading
 * inside an already-loaded page). Two thin shimmer bars.
 */
export function InlineSkeleton() {
  return (
    <div className="space-y-2 py-2">
      <Skeleton className="h-3 w-48" />
      <Skeleton className="h-3 w-32" />
    </div>
  );
}
